/**
 * MarkdownEditor — the CodeMirror 6 surface for `.md` files.
 *
 * The single way to view/edit markdown in the app: an Obsidian-style live
 * preview (added incrementally by the decoration phases) that is ALSO the edit
 * surface. Bound to the `WorkspaceStore` exactly like the Monaco `Editor`: it
 * renders a file's raw body from the VFS (`store.files`), pushes edits back via
 * `store.edit`, and recompiles flow through the model like any file change.
 *
 * CM6 loads lazily (dynamic `import()` of `./bundle`) so a composition that
 * never opens a `.md` pays nothing for it.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { parseMarkdownDocument } from "@archlang/engine";
import { useWorkspace } from "../../WorkspaceContext.js";
import { useWorkspaceFiles } from "../../WorkspaceStore.js";
import { useWorkspaceModel } from "../../WorkspaceModelContext.js";
import { makeAssetResolver } from "../../../documents/assets.js";
import { DocViewEditorPanel } from "../../../views/DocViewEditorPanel.js";
import type { MdController } from "./bundle.js";
import type { FlavoredContext } from "./flavored.js";
import type { MdLspContext } from "./lsp.js";
import { buildMdModelAssist } from "./refResolve.js";
import { computeDocRename } from "./renameDoc.js";

export interface MarkdownEditorProps {
  /** VFS path of the `.md` file this editor edits. */
  readonly path: string;
  /** Hard-lock typing (still live-previews). Default: editable. */
  readonly locked?: boolean;
  /** Route a pill click (`archlang://…` href) — module/process/doc/aspect nav. */
  readonly onArchlangNav?: (href: string) => void;
  /** Section slug to scroll to (deep-link / outline click). */
  readonly scrollToSlug?: string;
  /** Bumped to force a re-scroll to `scrollToSlug` even when it's unchanged. */
  readonly scrollNonce?: number;
  /** Content rendered at the TOP of CM's scroller, above the prose, so it
   *  scrolls WITH the document (one page, no separate fixed bar / separator)
   *  while CM keeps owning the scroll — virtualization stays intact. Portaled
   *  into `scrollDOM` before `.cm-content`; CM measures content from live rects
   *  so pushing it down stays pixel-exact. */
  readonly header?: ReactNode;
  readonly className?: string;
}

export function MarkdownEditor({
  path,
  locked = false,
  onArchlangNav,
  scrollToSlug,
  scrollNonce = 0,
  header,
  className,
}: MarkdownEditorProps): ReactElement {
  const store = useWorkspace();
  const files = useWorkspaceFiles(store);
  const content = files.get(path) ?? "";
  const { model, documents } = useWorkspaceModel();

  // Model-backed assistance (pill resolver + [[ref]] completions + hover).
  // Rebuilt per compile from one walk of the wire model.
  const assist = useMemo(
    () => (model ? buildMdModelAssist(model, documents) : undefined),
    [model, documents],
  );
  const resolveRef = assist?.resolveRef;
  // Relative `![](./img)` srcs resolve against the workspace asset channel.
  const assetResolver = useMemo(
    () => makeAssetResolver(() => store.getState().assets),
    [store],
  );
  // A value-stable signature of ONLY the `.arch` sources — the refresh key for
  // an embedded ```view (see FlavoredContext.workspaceVersion). Recomputes on
  // any file edit, but its VALUE is identical across prose edits, so the embed
  // rebuilds solely when arch content changes.
  const archVersion = useMemo(() => {
    let sig = "";
    for (const [k, v] of files) if (k.endsWith(".arch")) sig += `${k} ${v}`;
    return sig;
  }, [files]);

  const hostRef = useRef<HTMLDivElement>(null);
  const ctrlRef = useRef<MdController | null>(null);
  // A detached host the header is portaled into BEFORE the editor mounts, then
  // adopted by the block-widget as the document's first element — so CM measures
  // its true height on the first layout (the doc opens at the top, no jump).
  const headerHostRef = useRef<HTMLDivElement | null>(null);
  if (header && headerHostRef.current === null && typeof document !== "undefined") {
    const el = document.createElement("div");
    el.className = "cm-md-header-host";
    headerHostRef.current = el;
  }
  // Latest nav handler + resolver, read fresh through stable closures so a
  // changing prop / recompile doesn't force a remount.
  const navRef = useRef<typeof onArchlangNav>(onArchlangNav);
  navRef.current = onArchlangNav;
  const resolveRefRef = useRef<typeof resolveRef>(resolveRef);
  resolveRefRef.current = resolveRef;
  const assistRef = useRef<typeof assist>(assist);
  assistRef.current = assist;
  const documentsRef = useRef(documents);
  documentsRef.current = documents;

  // F2 — rename the document: ask for a new name, then rewrite every inbound
  // `[[ref]]` across the workspace + the doc's own title (or file name).
  const doRename = (): void => {
    if (locked) return;
    const stem = (path.split("/").pop() ?? path).replace(/\.(md|markdown)$/i, "");
    const oldName = documentsRef.current.find((d) => d.path === path)?.name ?? stem;
    const ask = (globalThis as { prompt?: (m: string, d?: string) => string | null }).prompt;
    const input = ask ? ask("Rename document", oldName) : null;
    const newName = input?.trim();
    if (!newName || newName === oldName) return;
    // Reject characters that would break a file path or a `[[ref]]`/section.
    if (/[/\\[\]#]/.test(newName)) return;
    const plan = computeDocRename(store.getState().files, oldName, newName, path);
    for (const [p, c] of plan.edits) store.edit(p, c);
    if (plan.rename) {
      store.renameFile(plan.rename.from, plan.rename.to);
      store.setActiveFile(plan.rename.to);
    }
  };

  // An open embedded-view builder: which fenced body it edits (doc offsets) +
  // that body's text. Host-rendered (see DocViewEditorPanel) so it survives the
  // widget rebuild each write triggers. The ref lets the (stable) write callback
  // read the live range without re-subscribing.
  const [viewEdit, setViewEdit] = useState<{ from: number; to: number; text: string } | null>(null);
  const viewEditRef = useRef(viewEdit);
  viewEditRef.current = viewEdit;
  // Write new block text over the current fenced-body range, then advance the
  // tracked range to the rewritten span (the builder keeps editing the SAME
  // block as it grows/shrinks).
  const writeView = useCallback((newText: string): void => {
    const cur = viewEditRef.current;
    const ctrl = ctrlRef.current;
    if (!cur || !ctrl) return;
    ctrl.view.dispatch({ changes: { from: cur.from, to: cur.to, insert: newText } });
    const next = { from: cur.from, to: cur.from + newText.length, text: newText };
    viewEditRef.current = next;
    setViewEdit(next);
  }, []);

  // A fresh context object each call — its identity change drives a decoration
  // rebuild (so labels refresh on recompile); the closures read the live refs.
  const makeCtx = (): FlavoredContext => ({
    onNav: (href) => navRef.current?.(href),
    resolveRef: (c, inner) => resolveRefRef.current?.(c, inner),
    resolveForeignLabel: (c, field) => assistRef.current?.resolveForeignLabel(c, field),
    resolveImage: (src) => assetResolver(src, path),
    // A ```view fenced block compiles its view against the LIVE workspace files
    // (read every call, so the embed projects the current model)…
    workspaceSources: () => store.getState().files,
    // …and refreshes ONLY when the .arch sources actually change (not on every
    // prose keystroke — see archVersion).
    workspaceVersion: archVersion,
    // The embed's Build chip opens the host-rendered WYSIWYG builder over the
    // fenced body (locked=read-only docs get none).
    onEditView: locked ? undefined : (range, blockText) => setViewEdit({ ...range, text: blockText }),
    onRename: () => doRename(),
  });
  const makeLspCtx = (): MdLspContext => ({
    completions: assistRef.current?.completions,
    hover: (inner) => assistRef.current?.hover(inner),
  });
  // The last text WE pushed to the store, so the store→content echo of our own
  // keystroke doesn't loop back into `setDoc`.
  const lastSent = useRef<string>("");

  // Scroll the editor to a section by slug (deep-link / outline click). Sections
  // are recomputed from the LIVE content so the anchor tracks edits.
  const slugRef = useRef(scrollToSlug);
  slugRef.current = scrollToSlug;
  const scrollToSection = (slug?: string): void => {
    const ctrl = ctrlRef.current;
    if (!ctrl || !slug) return;
    const content = store.getState().files.get(path) ?? "";
    const sec = parseMarkdownDocument(path, content).sections.find((s) => s.slug === slug);
    if (!sec) return;
    ctrl.scrollToLine(content.slice(0, sec.offset).split("\n").length - 1);
  };

  // Mount a fresh editor per file (new path = new document + history). CM6 is
  // imported here so it never enters a non-doc bundle.
  useEffect(() => {
    let alive = true;
    let ctrl: MdController | null = null;
    const host = hostRef.current;
    if (!host) return;
    const initial = store.getState().files.get(path) ?? "";
    lastSent.current = initial;
    void import("./bundle.js").then(({ mountMarkdown }) => {
      if (!alive || !hostRef.current) return;
      ctrl = mountMarkdown(hostRef.current, {
        doc: store.getState().files.get(path) ?? "",
        editable: !locked,
        // The pre-populated header host, adopted as the doc's first block. CM
        // owns its height in the height map (scroll / virtualization / tail all
        // correct natively), and it's already filled so the open lands at top.
        headerEl: headerHostRef.current ?? undefined,
        onChange: (c) => {
          lastSent.current = c;
          store.edit(path, c);
        },
        flavoredCtx: makeCtx(),
        lspCtx: makeLspCtx(),
      });
      ctrlRef.current = ctrl;
      // Honor a deep-link anchor present at first mount.
      if (slugRef.current) scrollToSection(slugRef.current);
    });
    return () => {
      alive = false;
      ctrl?.destroy();
      ctrlRef.current = null;
    };
    // `header` only gates whether a header widget exists; its content is portaled
    // separately, so a content change must NOT remount the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, locked, store]);

  // Initial height is correct (the host is pre-populated + adopted). But the
  // header's CONTENT can change height later (props panel toggle, title wrap);
  // CM doesn't observe that, so flush a measure after such a commit to re-read
  // the adopted subtree. Not needed on first mount, harmless there.
  useLayoutEffect(() => {
    if (headerHostRef.current) ctrlRef.current?.view.requestMeasure();
  }, [header]);

  // Sync external content changes (bulk reload / programmatic) into the editor,
  // skipping the echo of our own edits.
  useEffect(() => {
    const ctrl = ctrlRef.current;
    if (!ctrl) return;
    if (content === lastSent.current) return;
    lastSent.current = content;
    ctrl.setDoc(content);
  }, [content]);

  // Recompile → refresh the flavored + LSP contexts (labels, completions, hover).
  useEffect(() => {
    ctrlRef.current?.setFlavoredContext(makeCtx());
    ctrlRef.current?.setLspContext(makeLspCtx());
    // makeCtx/makeLspCtx read refs; the `assist` identity is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assist]);

  // Deep-link / outline scroll — re-fires on each nonce bump (repeat clicks).
  useEffect(() => {
    scrollToSection(scrollToSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollNonce, scrollToSlug]);

  return (
    <>
      <div ref={hostRef} className={className} data-testid="markdown-editor">
        {/* Portal into the detached host so it's populated BEFORE the editor
            adopts it — CM then measures the real height on the first layout. */}
        {header && headerHostRef.current ? createPortal(header, headerHostRef.current) : null}
      </div>
      {/* The embedded-view builder — host-rendered (outside the CM6 widget it
          edits), so it persists as each write rebuilds the widget. */}
      {viewEdit && (
        <DocViewEditorPanel
          blockText={viewEdit.text}
          onWrite={writeView}
          onClose={() => setViewEdit(null)}
        />
      )}
    </>
  );
}
